/**
 * Pure mapping from the realtime `AppEventPayload` location arms (the decoded proto
 * shape) to the realtime event names + payloads the artboard/sidebar consumers read
 * (`category-created`, `category-updated`, `category-deleted`).
 *
 * The account-scoped app-events stream delivers events for all of the user's projects;
 * `shouldDispatch` filters to the joined project so a foreign project's event is not
 * dispatched. It is the single source of truth for that filter so the dispatch decision
 * is testable without a live socket.
 *
 * `created`/`updated` arms carry `{ projectId, location }` where `location` is a
 * VisionLocation proto; `deleted` carries `{ projectId, locationId }`. The image arm
 * carries `{ projectId, image }` where `image` is a ProjectImage proto, mapped onto the
 * legacy `image-uploaded` event (`addImage` dedupes by id, so the uploader's own echo is
 * absorbed on top of its optimistic add).
 */

import { locationToCategory } from './visionLocationsMapping.js';
import { projectImageToLegacy } from './visionAssetsMapping.js';
import { inviteViewFromProto, memberViewFromProto } from './visionMembershipMapping.js';
import { boardGenerationToLegacy, cropToLegacy, generationLikesToLegacy, generationToLegacy } from './visionGenerationsMapping.js';
import { fromProtoGradingSettings } from './gradingSettingsMapping.js';
import { chatMessageToLegacy } from './visionChatMapping.js';
import { regionsToLegacy } from './visionMaskRegionsMapping.js';
import {
  castToLegacy,
  configToLegacy,
  presetToLegacy,
  propToLegacy,
  shotToLegacy,
} from './visionShotlistMapping.js';

/**
 * Whether an incoming location event belongs to the joined project and should be
 * dispatched. The account stream delivers other projects' events; the single-project
 * stores would be corrupted by a foreign project's event, so those are dropped.
 */
export function shouldDispatch(eventProjectId, currentProjectId) {
  return Boolean(currentProjectId) && eventProjectId === currentProjectId;
}

export function locationCreatedToEvent({ projectId, location }) {
  return {
    event: 'category-created',
    payload: { category: locationToCategory(location, projectId) },
  };
}

export function locationUpdatedToEvent({ location }) {
  return {
    event: 'category-updated',
    payload: {
      categoryId: location.locationId,
      updates: { name: location.name, address: location.address },
    },
  };
}

export function inviteCreatedToEvent({ invite }) {
  return {
    event: 'invite-created',
    payload: { invite: inviteViewFromProto(invite) },
  };
}

export function inviteDeletedToEvent({ inviteId }) {
  return {
    event: 'invite-cancelled',
    payload: { inviteId },
  };
}

export function memberJoinedToEvent({ member }) {
  const mapped = memberViewFromProto(member);
  return {
    event: 'member-joined',
    payload: {
      user: { id: mapped.user_id, display_name: mapped.display_name },
      role: mapped.role,
    },
  };
}

export function memberRemovedToEvent({ accountId }) {
  return {
    event: 'member-removed',
    payload: { userId: accountId },
  };
}

export function memberRoleChangedToEvent({ member }) {
  const mapped = memberViewFromProto(member);
  return {
    event: 'member-role-updated',
    payload: { userId: mapped.user_id, role: mapped.role },
  };
}

export function ownershipTransferredToEvent({ oldOwnerAccountId, newOwnerAccountId }) {
  return {
    event: 'ownership-transferred',
    payload: { oldOwnerId: oldOwnerAccountId, newOwnerId: newOwnerAccountId },
  };
}

export function locationDeletedToEvent({ locationId, imageIds }) {
  // Deleting a location cascade-deletes its images; the event carries their ids so a passive
  // viewer prunes them from its store.
  return {
    event: 'category-deleted',
    payload: { categoryId: locationId, imageIds: imageIds ?? [] },
  };
}

export function projectImageCreatedToEvent({ image }) {
  return {
    event: 'image-uploaded',
    payload: { image: projectImageToLegacy(image) },
  };
}

export function imageRotatedToEvent({ image }) {
  return {
    event: 'image-rotated',
    payload: { image: projectImageToLegacy(image) },
  };
}

// An empty location_id is the proto default for "uncategorized" — map it to a null
// category_id so the artboard places the image in the Uncategorized bucket.
function locationIdToCategoryId(locationId) {
  return locationId ? locationId : null;
}

export function imageCategorizedToEvent({ assetId, locationId }) {
  return {
    event: 'image-categorized',
    payload: { imageId: assetId, categoryId: locationIdToCategoryId(locationId) },
  };
}

export function imagesBulkCategorizedToEvent({ assignments }) {
  return {
    event: 'images-bulk-categorized',
    payload: {
      assignments: (assignments ?? []).map((assignment) => ({
        imageId: assignment.assetId,
        categoryId: locationIdToCategoryId(assignment.locationId),
      })),
    },
  };
}

export function projectImageMovedToEvent({ imageId, toLocationId }) {
  return {
    event: 'image-moved',
    payload: { imageId, newCategoryId: locationIdToCategoryId(toLocationId) },
  };
}

export function projectImagesBulkMovedToEvent({ imageIds, toLocationId }) {
  return {
    event: 'images-bulk-moved',
    payload: { imageIds: imageIds ?? [], categoryId: locationIdToCategoryId(toLocationId) },
  };
}

export function projectImageDeletedToEvent({ imageId }) {
  return {
    event: 'image-deleted',
    payload: { imageId },
  };
}

export function uncategorizedImagesDeletedToEvent({ imageIds }) {
  // Deleting all uncategorized images carries their ids so a passive viewer prunes them in one pass.
  return {
    event: 'images-deleted',
    payload: { imageIds: imageIds ?? [] },
  };
}

export function generationStartedToEvent({ generation }) {
  const legacy = generationToLegacy(generation);
  return {
    event: 'generation-started',
    payload: {
      imageId: legacy.image_id,
      generationId: legacy.id,
      userId: legacy.generated_by,
      displayName: legacy.display_name,
      avatarPath: legacy.avatar_path,
      userPrompt: legacy.user_prompt,
      aspectRatio: legacy.aspect_ratio,
      parentGenerationId: legacy.parent_generation_id,
      colorRefPath: legacy.color_ref_path,
      additionalRefPath: legacy.additional_ref_path,
      generationParams: legacy.generation_params,
    },
  };
}

export function generationCompletedToEvent({ generation }) {
  return {
    event: 'generation-complete',
    payload: { generation: generationToLegacy(generation) },
  };
}

export function generationUpdatedToEvent({ generation }) {
  return {
    event: 'grading-updated',
    payload: {
      imageId: generation.imageId,
      generationId: generation.generationId,
      gradingSettings: fromProtoGradingSettings(generation.gradingSettings),
      gradedPath: generation.gradedUrl || null,
      gradedThumbnailPath: generation.gradedThumbnailUrl || null,
      displayPath: generation.displayUrl || null,
    },
  };
}

export function generationFailedToEvent({ generationId, imageId, errorMessage }) {
  return {
    event: 'generation-failed',
    payload: {
      imageId,
      generationId,
      error: errorMessage || 'Generation failed. Please try again.',
    },
  };
}

export function generationLikesUpdatedToEvent({ generationId, imageId, likes }) {
  return {
    event: 'generation-like-updated',
    payload: {
      generationId,
      imageId,
      likes: generationLikesToLegacy(likes ?? []),
    },
  };
}

export function generationDeletedToEvent({ imageId, generationId }) {
  return {
    event: 'generation-deleted',
    payload: { imageId, generationId },
  };
}

export function generationCropUpdatedToEvent({ imageId, generationId, crop }) {
  return {
    event: 'crop-updated',
    payload: {
      imageId,
      generationId,
      cropParams: cropToLegacy(crop),
    },
  };
}

export function imageMaskRegionsUpdatedToEvent({ imageId, updatedByAccountId, regions }) {
  return {
    event: 'image-mask-regions-updated',
    payload: {
      imageId,
      updatedBy: updatedByAccountId,
      regions: regionsToLegacy(regions),
    },
  };
}

export function generationMaskRegionsUpdatedToEvent({ generationId, updatedByAccountId, regions }) {
  return {
    event: 'generation-mask-regions-updated',
    payload: {
      generationId,
      updatedBy: updatedByAccountId,
      regions: regionsToLegacy(regions),
    },
  };
}

export function displaySelectionChangedToEvent({
  imageId,
  generationId,
  generatedThumbnailUrl,
  gradedThumbnailUrl,
  crop,
  displayUserLocked,
}) {
  return {
    event: 'display-selection-changed',
    payload: {
      imageId,
      generationId: generationId || null,
      generatedThumbnailPath: generatedThumbnailUrl || null,
      gradedThumbnailPath: gradedThumbnailUrl || null,
      cropParams: cropToLegacy(crop),
      displayUserLocked,
    },
  };
}

export function imagesReorderedToEvent({ imageIds }) {
  return {
    event: 'images-reordered',
    payload: { imageIds: imageIds ?? [] },
  };
}

export function creditsUpdatedToEvent({ projectId, projectCredits }) {
  return {
    event: 'credits-updated',
    payload: { projectId, pool: 'project', balance_after: Number(projectCredits) },
  };
}

export function chatMessageCreatedToEvent({ message }) {
  return {
    event: 'chat-message',
    payload: { message: chatMessageToLegacy(message) },
  };
}

export function chatMessageDeletedToEvent({ imageId, messageId }) {
  return {
    event: 'chat-message-deleted',
    payload: { messageId, imageId },
  };
}

export function isBoardGeneration(generation) {
  return !generation?.imageId;
}

export function boardGenerationStartedToEvent({ generation }) {
  const legacy = boardGenerationToLegacy(generation);
  return {
    event: 'visionboard-generation-started',
    payload: {
      projectId: legacy.project_id,
      generationId: legacy.id,
      userId: legacy.generated_by,
      displayName: legacy.display_name,
      avatarPath: legacy.avatar_path,
      userPrompt: legacy.user_prompt,
      aspectRatio: legacy.aspect_ratio,
      generationParams: legacy.generation_params,
      generatedAt: legacy.generated_at,
      additionalRefPath: legacy.additional_ref_path,
      additionalRefIds: legacy.additional_ref_ids,
      userRules: legacy.user_rules,
    },
  };
}

export function boardGenerationCompletedToEvent({ generation }) {
  return {
    event: 'visionboard-generation-complete',
    payload: { generation: boardGenerationToLegacy(generation) },
  };
}

export function boardGenerationFailedToEvent({ generationId, projectId, errorMessage }) {
  return {
    event: 'visionboard-generation-failed',
    payload: {
      projectId,
      generationId,
      error: errorMessage || 'Generation failed. Please try again.',
    },
  };
}

export function boardGenerationDeletedToEvent({ projectId, generationId }) {
  return {
    event: 'visionboard-generation-deleted',
    payload: { projectId, generationId },
  };
}

export function projectSettingsUpdatedToEvent({ projectId, name, description }) {
  return {
    event: 'project-updated',
    payload: { projectId, name, description },
  };
}

export function projectDeletedToEvent({ projectId }) {
  return {
    event: 'project-deleted',
    payload: { projectId },
  };
}

export function accountDeletedToEvent({ accountId }) {
  return {
    event: 'account-deleted',
    payload: { accountId },
  };
}

export function shotlistShotsCreatedToEvent({ shots }) {
  return {
    event: 'shotlist-shots-created',
    payload: { shots: (shots ?? []).map(shotToLegacy) },
  };
}

export function shotlistShotUpdatedToEvent({ shot }) {
  return {
    event: 'shotlist-shot-updated',
    payload: { shot: shotToLegacy(shot) },
  };
}

export function shotlistShotDeletedToEvent({ shotId }) {
  return {
    event: 'shotlist-shot-deleted',
    payload: { shotId },
  };
}

export function shotlistShotHiddenToEvent({ shotId, imageId }) {
  return {
    event: 'shotlist-shot-hidden',
    payload: { shotId, imageId },
  };
}

export function shotlistCastCreatedToEvent({ cast }) {
  return {
    event: 'cast-created',
    payload: { cast: castToLegacy(cast) },
  };
}

export function shotlistCastUpdatedToEvent({ cast }) {
  return {
    event: 'cast-updated',
    payload: { cast: castToLegacy(cast) },
  };
}

export function shotlistCastDeletedToEvent({ castId }) {
  return {
    event: 'cast-deleted',
    payload: { castId },
  };
}

export function shotlistPropCreatedToEvent({ prop }) {
  return {
    event: 'prop-created',
    payload: { prop: propToLegacy(prop) },
  };
}

export function shotlistPropDeletedToEvent({ propId, propName }) {
  return {
    event: 'prop-deleted',
    payload: { propId, propName },
  };
}

export function shotlistPresetCreatedToEvent({ category, preset }) {
  return {
    event: 'preset-created',
    payload: { preset: presetToLegacy(category, preset) },
  };
}

export function shotlistConfigUpdatedToEvent({ config }) {
  return {
    event: 'shotlist-config-updated',
    payload: configToLegacy(config),
  };
}

export function imageDescriptionUpdatedToEvent({ imageId, description }) {
  return {
    event: 'image-description-updated',
    payload: { imageId, description },
  };
}

export function imageFrameUpdatedToEvent({ imageId, frameNumber }) {
  return {
    event: 'image-frame-updated',
    payload: { imageId, frameNumber },
  };
}
